/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { MutableRef } from 'preact/hooks';
import type { Keys } from '#utils/UNSAFE_keys';
import type { TableSelectionDetail, TableSelectionMode } from '#UNSAFE_TableView';
import type { ActiveCell, ActiveCellDetail, ScrollDetail, TableColumn } from '../Table';

import { keyExtractor } from '#utils/PRIVATE_collectionUtils';
import { addKey, containsKey, isSameKey, removeKey } from '#utils/UNSAFE_keys';
import { getCellType, getRowKey } from '#PRIVATE_TableCommon/utils/DomUtils';
import {
  getColumnIndexForHeaderFooterElement,
  getElementFromCell,
  getRowIndexForRowElement,
  getRowKeyForRowIndex,
  logicalCellExtractor,
  TABLE_FOCUSABLE_SELECTOR,
  TABLE_ROW_SELECTOR
} from './TableDomUtils';

/**
 * Helper function to handle a selection gesture (click, spacebar, etc...)
 */
export function handleSelectionGesture<K extends string | number, D, C extends string>(
  rootElement: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  targetElement: HTMLElement,
  isModifierKey: boolean,
  onSelectionChange: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void,
  isDropDownSelection: boolean,
  selectionMode?: TableSelectionMode,
  selected?: { row?: Keys<K>; column?: Keys<C> }
) {
  const emptyKeys = { all: false, keys: new Set() };
  let selectedRows = selected?.row != null ? selected.row : (emptyKeys as Keys<K>);
  let selectedColumns = selected?.column != null ? selected.column : (emptyKeys as Keys<C>);

  const newRowKey = keyExtractor<K>(targetElement, `[${TABLE_ROW_SELECTOR}='${tableId}']`);
  if (newRowKey != null) {
    if (
      selectionMode?.row === 'single' ||
      selectionMode?.row === 'multiple' ||
      selectionMode?.row === 'multipleToggle'
    ) {
      const isToggleSelectionMode = isDropDownSelection || selectionMode.row === 'multipleToggle';
      const isMultiSelectRowGesture = isModifierKey || isToggleSelectionMode;
      if (selectedRows == null) {
        selectedRows = { all: false, keys: new Set([newRowKey]) };
      } else if (!containsKey(selectedRows, newRowKey)) {
        if (isMultiSelectRowGesture && selectionMode.row !== 'single') {
          selectedRows = addKey(selectedRows, newRowKey);
        } else {
          selectedRows = { all: false, keys: new Set([newRowKey]) };
        }
      } else if (isMultiSelectRowGesture) {
        selectedRows = removeKey(selectedRows, newRowKey, false);
      } else {
        selectedRows = { all: false, keys: new Set([newRowKey]) };
      }
      selectedColumns = emptyKeys as Keys<C>;
    }
  } else if (selectionMode?.column === 'single' || selectionMode?.column === 'multiple') {
    const cellElement = targetElement.closest(
      `[${TABLE_FOCUSABLE_SELECTOR}='${tableId}']`
    ) as HTMLElement;
    if (cellElement != null) {
      const cellType = getCellType(cellElement);
      if (cellType === 'header' || cellType === 'footer') {
        const index = getColumnIndexForHeaderFooterElement(
          rootElement,
          tableId,
          cellElement,
          cellType === 'header'
        );
        if (index != null) {
          const column = columnsArray[index];
          if (column.value.selectable !== 'disabled') {
            if (selectedColumns == null) {
              selectedColumns = { all: false, keys: new Set([column.key]) };
            } else if (!containsKey(selectedColumns, column.key)) {
              if (isModifierKey && selectionMode.column === 'multiple') {
                selectedColumns = addKey(selectedColumns, column.key);
              } else {
                selectedColumns = { all: false, keys: new Set([column.key]) };
              }
            } else if (isModifierKey) {
              selectedColumns = removeKey(selectedColumns, column.key, false);
            } else {
              selectedColumns = { all: false, keys: new Set([column.key]) };
            }
            selectedRows = emptyKeys as Keys<K>;
          }
        }
      }
    }
  }

  if (!isSameKey(selectedRows, selected?.row) || !isSameKey(selectedColumns, selected?.column)) {
    onSelectionChange(
      { value: { row: selectedRows, column: selectedColumns }, target: targetElement },
      false
    );
  }
}

/**
 * Helper function to calculate and apply a new selection state corresponding to the range provided
 */
function _applyRangeSelection<K extends string | number, D, C extends string>(
  rootElement: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  targetElement: HTMLElement,
  startIndex: number,
  endIndex: number,
  isRowGesture: boolean,
  onSelectionChange: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void,
  isDropDownSelection: boolean,
  selectionMode?: TableSelectionMode,
  selected?: { row?: Keys<K>; column?: Keys<C> },
  previousEndIndex?: number
) {
  const emptyKeys = { all: false, keys: new Set() };
  let selectedRows = selected?.row != null ? selected.row : (emptyKeys as Keys<K>);
  let selectedColumns = selected?.column != null ? selected.column : (emptyKeys as Keys<C>);

  if (isRowGesture) {
    selectedColumns = emptyKeys as Keys<C>;
    if (selectionMode?.row === 'multiple' || selectionMode?.row === 'multipleToggle') {
      const isToggleSelectionMode = isDropDownSelection || selectionMode.row === 'multipleToggle';
      selectedRows = isToggleSelectionMode ? selectedRows : (emptyKeys as Keys<K>);
      // selected rows should be added in the order that they are selected
      if (startIndex <= endIndex) {
        for (let i = startIndex; i <= endIndex; i++) {
          const key = getRowKeyForRowIndex<K>(rootElement, tableId, i);
          selectedRows = addKey(selectedRows, key) as Keys<K>;
        }
        // toggle selection gesture is additive except for rows added from previous gesture
        if (isToggleSelectionMode && previousEndIndex != null) {
          if (previousEndIndex > endIndex) {
            for (let i = previousEndIndex; i > endIndex; i--) {
              const key = getRowKeyForRowIndex<K>(rootElement, tableId, i);
              selectedRows = removeKey(selectedRows, key, false) as Keys<K>;
            }
          } else if (previousEndIndex < startIndex) {
            for (let i = previousEndIndex; i < startIndex; i++) {
              const key = getRowKeyForRowIndex<K>(rootElement, tableId, i);
              selectedRows = removeKey(selectedRows, key, false) as Keys<K>;
            }
          }
        }
      } else {
        for (let i = startIndex; i >= endIndex; i--) {
          const key = getRowKeyForRowIndex<K>(rootElement, tableId, i);
          selectedRows = addKey(selectedRows, key) as Keys<K>;
        }
        // toggle selection gesture is additive except for rows added from previous gesture
        if (isToggleSelectionMode && previousEndIndex != null) {
          if (previousEndIndex < endIndex) {
            for (let i = previousEndIndex; i < endIndex; i++) {
              const key = getRowKeyForRowIndex<K>(rootElement, tableId, i);
              selectedRows = removeKey(selectedRows, key, false) as Keys<K>;
            }
          } else if (previousEndIndex > startIndex) {
            for (let i = previousEndIndex; i > startIndex; i--) {
              const key = getRowKeyForRowIndex<K>(rootElement, tableId, i);
              selectedRows = removeKey(selectedRows, key, false) as Keys<K>;
            }
          }
        }
      }
    }
  } else {
    selectedRows = emptyKeys as Keys<K>;
    if (selectionMode?.column === 'multiple') {
      selectedColumns = emptyKeys as Keys<C>;
      // selected columns should be added in the order that they are selected
      if (startIndex <= endIndex) {
        for (let i = startIndex; i <= endIndex; i++) {
          const column = columnsArray[i];
          if (column.value.selectable !== 'disabled') {
            selectedColumns = addKey(selectedColumns, column.key);
          }
        }
      } else {
        for (let i = startIndex; i >= endIndex; i--) {
          const column = columnsArray[i];
          if (column.value.selectable !== 'disabled') {
            selectedColumns = addKey(selectedColumns, column.key);
          }
        }
      }
    }
  }

  if (!isSameKey(selectedRows, selected?.row) || !isSameKey(selectedColumns, selected?.column)) {
    onSelectionChange(
      { value: { row: selectedRows, column: selectedColumns }, target: targetElement },
      true
    );
  }
}

/**
 * Determines whether a potential range selection gesture is valid based on the given target
 */
export function isRangeSelectionGesture<K extends string | number, D, C extends string>(
  rootElement: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  targetElement: HTMLElement,
  activeCell: ActiveCell<K, C>,
  selectionMode?: TableSelectionMode
) {
  const targetCell = logicalCellExtractor<K, D, C>(targetElement, tableId, columnsArray);
  if (
    (((selectionMode?.row === 'multiple' || selectionMode?.row === 'multipleToggle') &&
      activeCell.type === 'data') ||
      (selectionMode?.column === 'multiple' &&
        (activeCell.type === 'header' || activeCell.type === 'footer'))) &&
    targetCell?.type === activeCell.type
  ) {
    const activeCellElement = getElementFromCell(rootElement, tableId, activeCell);
    const targetCellElement = getElementFromCell(rootElement, tableId, targetCell);
    if (activeCellElement != null && targetCellElement != null) {
      const isRow = targetCell.type === 'data';
      if (isRow) {
        return (
          getRowIndexForRowElement(
            rootElement,
            tableId,
            activeCellElement.parentElement as HTMLElement
          ) != null &&
          getRowIndexForRowElement(
            rootElement,
            tableId,
            targetCellElement.parentElement as HTMLElement
          ) != null
        );
      } else if (targetCell.type === 'header' || targetCell.type === 'footer') {
        const isHeader = targetCell.type === 'header';
        return (
          getColumnIndexForHeaderFooterElement(rootElement, tableId, activeCellElement, isHeader) !=
            null &&
          getColumnIndexForHeaderFooterElement(rootElement, tableId, targetCellElement, isHeader) !=
            null
        );
      }
    }
  }
  return false;
}

/**
 * Helper function to handle a range selection gesture (shift+click)
 */
export function handleRangeSelectionGesture<K extends string | number, D, C extends string>(
  rootElement: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  targetElement: HTMLElement,
  activeCell: ActiveCell<K, C>,
  onActiveCellChanged: (
    detail: ActiveCellDetail<K, C>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void,
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>,
  onSelectionChange: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void,
  isDropDownSelection: boolean,
  selectionMode?: TableSelectionMode,
  selected?: { row?: Keys<K>; column?: Keys<C> }
) {
  const targetCell = logicalCellExtractor<K, D, C>(targetElement, tableId, columnsArray);
  if (targetCell != null) {
    const activeCellElement = getElementFromCell(rootElement, tableId, activeCell);
    const targetCellElement = getElementFromCell(rootElement, tableId, targetCell);
    if (activeCellElement != null && targetCellElement != null) {
      if (
        (((selectionMode?.row === 'multiple' || selectionMode?.row === 'multipleToggle') &&
          targetCell.type === 'data') ||
          (selectionMode?.column === 'multiple' &&
            (targetCell.type === 'header' || targetCell.type === 'footer'))) &&
        targetCell.type === activeCell.type
      ) {
        let startIndex;
        let endIndex;
        let previousEndIndex;
        let endElement;
        const isToggleSelectionMode = isDropDownSelection || selectionMode.row === 'multipleToggle';
        const isRow = targetCell.type === 'data';
        if (isRow) {
          endElement = targetCellElement.parentElement as HTMLElement;
          const startElement =
            isToggleSelectionMode && selectionExtensionElementRef.current != null
              ? selectionExtensionElementRef.current
              : (activeCellElement.parentElement as HTMLElement);
          startIndex = getRowIndexForRowElement(rootElement, tableId, startElement);
          endIndex = getRowIndexForRowElement(rootElement, tableId, endElement);
          const previousElement = isToggleSelectionMode
            ? (activeCellElement.parentElement as HTMLElement)
            : selectionExtensionElementRef.current;
          if (previousElement != null) {
            previousEndIndex = getRowIndexForRowElement(rootElement, tableId, previousElement);
          }
        } else if (targetCell.type === 'header' || targetCell.type === 'footer') {
          const isHeader = targetCell.type === 'header';
          endElement = targetCellElement;
          startIndex = getColumnIndexForHeaderFooterElement(
            rootElement,
            tableId,
            activeCellElement,
            isHeader
          );
          endIndex = getColumnIndexForHeaderFooterElement(
            rootElement,
            tableId,
            targetCellElement,
            isHeader
          );
        }
        if (startIndex != null && endIndex != null) {
          if (!isRow || !isToggleSelectionMode) {
            selectionExtensionElementRef.current = endElement;
          } else {
            if (selectionExtensionElementRef.current == null) {
              selectionExtensionElementRef.current = activeCellElement.parentElement as HTMLElement;
            }
            onActiveCellChanged({ value: targetCell }, false, true);
            targetCellElement.focus({ preventScroll: true });
          }
          _applyRangeSelection(
            rootElement,
            tableId,
            columnsArray,
            targetElement,
            startIndex,
            endIndex,
            isRow,
            onSelectionChange,
            isDropDownSelection,
            selectionMode,
            selected,
            previousEndIndex
          );
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Helper function to handle a selection extension gesture (shift+arrow key)
 */
export function handleExtendSelectionGesture<K extends string | number, D, C extends string>(
  rootElement: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  targetElement: HTMLElement,
  isPrevious: boolean,
  activeCell: ActiveCell<K, C>,
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>,
  onSelectionChange: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void,
  isDropDownSelection: boolean,
  updateDeferredScrollDetail: (scrollDetail: ScrollDetail<K, C>) => void,
  selectionMode?: TableSelectionMode,
  selected?: { row?: Keys<K>; column?: Keys<C> }
) {
  const activeCellElement = getElementFromCell(rootElement, tableId, activeCell);
  if (activeCellElement != null) {
    let startIndex;
    let startElement;
    const isToggleSelectionMode = isDropDownSelection || selectionMode?.row === 'multipleToggle';
    const isRow = activeCell.type === 'data';
    const isHeader = activeCell.type === 'header';
    if (isRow) {
      startElement =
        isToggleSelectionMode && selectionExtensionElementRef.current != null
          ? selectionExtensionElementRef.current
          : (activeCellElement.parentElement as HTMLElement);
      startIndex = getRowIndexForRowElement(rootElement, tableId, startElement);
    } else {
      startElement = activeCellElement;
      startIndex = getColumnIndexForHeaderFooterElement(
        rootElement,
        tableId,
        startElement,
        isHeader
      );
    }
    if (startIndex != null) {
      let endIndex;
      let previousEndIndex;
      let extendedElement;
      const cursorElement =
        isToggleSelectionMode && isRow
          ? (activeCellElement.parentElement as HTMLElement)
          : selectionExtensionElementRef.current;
      if (cursorElement != null) {
        previousEndIndex = isRow
          ? getRowIndexForRowElement(rootElement, tableId, cursorElement)
          : getColumnIndexForHeaderFooterElement(rootElement, tableId, cursorElement, isHeader);
        extendedElement = isPrevious
          ? (cursorElement.previousElementSibling as HTMLElement)
          : (cursorElement.nextElementSibling as HTMLElement);
        if (extendedElement != null && (!isRow || (isRow && getRowKey(extendedElement) != null))) {
          if (!isToggleSelectionMode || !isRow) {
            selectionExtensionElementRef.current = extendedElement;
          } else if (selectionExtensionElementRef.current == null) {
            selectionExtensionElementRef.current = activeCellElement.parentElement as HTMLElement;
          }
          endIndex = isRow
            ? getRowIndexForRowElement(rootElement, tableId, extendedElement)
            : getColumnIndexForHeaderFooterElement(rootElement, tableId, extendedElement, isHeader);
        } else {
          endIndex = isRow
            ? getRowIndexForRowElement(rootElement, tableId, cursorElement)
            : getColumnIndexForHeaderFooterElement(rootElement, tableId, cursorElement, isHeader);
        }
      } else {
        extendedElement = isPrevious
          ? (startElement.previousElementSibling as HTMLElement)
          : (startElement.nextElementSibling as HTMLElement);
        if (extendedElement != null && (!isRow || (isRow && getRowKey(extendedElement) != null))) {
          selectionExtensionElementRef.current = extendedElement;
          endIndex = isRow
            ? getRowIndexForRowElement(rootElement, tableId, extendedElement)
            : getColumnIndexForHeaderFooterElement(rootElement, tableId, extendedElement, isHeader);
        } else {
          endIndex = startIndex;
        }
      }
      if (endIndex != null) {
        _applyRangeSelection(
          rootElement,
          tableId,
          columnsArray,
          targetElement,
          startIndex,
          endIndex,
          isRow,
          onSelectionChange,
          isDropDownSelection,
          selectionMode,
          selected,
          previousEndIndex
        );
      }

      // scroll 'extensionElement' into view if focus is not shifted
      if (!isRow || !isToggleSelectionMode) {
        const extensionElement =
          selectionExtensionElementRef.current != null
            ? selectionExtensionElementRef.current
            : activeCellElement;
        let extensionCell;
        if (isRow) {
          const rowKey = keyExtractor<K>(extensionElement, `[${TABLE_ROW_SELECTOR}='${tableId}']`);
          if (rowKey != null) {
            extensionCell = {
              type: activeCell.type,
              rowKey: rowKey,
              columnKey: activeCell.columnKey
            };
          }
        } else {
          extensionCell = logicalCellExtractor<K, D, C>(extensionElement, tableId, columnsArray);
        }
        if (extensionCell != null) {
          updateDeferredScrollDetail({
            cell: extensionCell,
            locationX: 'inView',
            locationY: 'inView'
          });
        }
      }
    }
  }
}
